import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Stack,
  Divider,
  useMediaQuery
} from "@mui/material";
import { motion } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "react-router-dom";
import CustomButton from "./CustomButton";
import AdmitCardBanner from "../assets/AdmitCard.png";
import ReportsBanner from "../assets/Reports.png";
import OnlineExamsBanner from "../assets/OnlineExams.png";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import QuizIcon from "@mui/icons-material/Quiz";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useTheme } from "@mui/material/styles";


const ExamAbout = () => {
    const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const examImages = [
    {
      id: 1,
      imageUrl: OnlineExamsBanner,
    },
    {
      id: 2,
      imageUrl: AdmitCardBanner,
    },
    {
      id: 3,
      imageUrl: ReportsBanner,
    },
  ];

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    dotsClass: "slick-dots custom-dots",
  };

  const steps = [
    { label: "Registration", icon: <HowToRegIcon />, color: "#065A79" },
    { label: "Admit Card", icon: <PictureAsPdfIcon />, color: "#AD1C86" },
    { label: "Online Exam", icon: <QuizIcon />, color: "#FF9800" },
    { label: "Results", icon: <AssessmentIcon />, color: "#4CAF50" },
    { label: "Top Reports", icon: <EmojiEventsIcon />, color: "#065A79" },
  ];

  const features = [
    {
      icon: <HowToRegIcon sx={{ fontSize: 40 }} />,
      title: "Easy Registration",
      description: "Register for exams in just a few clicks",
      color: "#065A79",
    },
    {
      icon: <PictureAsPdfIcon sx={{ fontSize: 40 }} />,
      title: "Instant Admit Card",
      description: "Download admit card immediately",
      color: "#AD1C86",
    },
    {
      icon: <QuizIcon sx={{ fontSize: 40 }} />,
      title: "Smooth Examination",
      description: "User-friendly interface with timer",
      color: "#FF9800",
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      title: "Instant Results",
      description: "Get detailed performance reports",
      color: "#4CAF50",
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
      title: "Leaderboard",
      description: "Check top performers ranking",
      color: "#AD1C86",
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 40 }} />,
      title: "Performance Analytics",
      description: "Track your progress over time",
      color: "#065A79",
    },
  ];

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)",
        pt: 8,
        pb: 8,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(6,90,121,0.1) 0%, rgba(173,28,134,0.05) 100%)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#AD1C86",
                fontWeight: "bold",
                letterSpacing: 3,
                mb: 2,
                display: "block",
              }}
            >
              Digital Examination Platform
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: "bold",
                background: "linear-gradient(135deg, #065A79 0%, #AD1C86 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 2,
                fontSize: "2.4rem",
                fontOpticalSizing: "auto",
                fontStyle: "normal",
                letterSpacing: 1,
              }}
            >
              Online Examination System
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#666",
                maxWidth: 800,
                mx: "auto",
                mb: 3,
                fontFamily: "Poppins, sans-serif",
                fontStyle: "normal",
              }}
            >
              Your Complete Digital Examination Journey
            </Typography>
            <Divider
              sx={{
                width: 100,
                mx: "auto",
                borderColor: "#AD1C86",
                borderWidth: 2,
              }}
            />
          </motion.div>
        </Box>
        {/* Exam content */}
        <Box sx={{ pt: 3, pb: 5 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: "#065A79",
                    mb: 2,
                    fontSize: "2rem",
                    fontFamily: "Poppins, sans-serif",
                    fontOpticalSizing: "auto",
                    fontStyle: "normal",
                    letterSpacing: 1,
                  }}
                >
                  Digital Examination & Student Assessment Portal
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Montserrat, sans-serif",
                    fontOpticalSizing: "auto",
                    fontStyle: "normal",
                    letterSpacing: 1,
                    lineHeight: "1.8rem",
                    fontSize: 15,
                  }}
                >
                  Welcome to the official online examination platform of Shree
                  Mangalam School. This portal enables students to download
                  admit cards, appear in online examinations, view detailed
                  report cards, and track their academic performance anytime and
                  anywhere.
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    mt: 4,
                    p: 2,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #065A7910, #AD1C8610)",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#AD1C86",
                      fontWeight: "bold",
                      mb: 1,
                      fontFamily: "Montserrat, sans-serif",
                      fontOpticalSizing: "auto",
                      fontStyle: "normal",
                      letterSpacing: 1,
                    }}
                  >
                    Excellence Through Education
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontOpticalSizing: "auto",
                      fontStyle: "normal",
                      letterSpacing: 1,
                      lineHeight: "1.8rem",
                      fontSize: 15,
                    }}
                  >
                    Shree Mangalam School is committed to providing quality
                    education and modern digital learning experiences that help
                    students achieve academic excellence and overall growth.
                  </Typography>
                  <Link to="/online-exams" style={{ textDecoration: "none" }}>
                    <CustomButton ButtonName={"Register Now"} />
                  </Link>
                </Paper>
              </motion.div>
            </Grid>
            {/* Slider Section */}
            <Grid item xs={12} md={6} >
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    borderRadius: 4,
                //   height: { xs: 300, md: 450 },
                    overflow: "hidden",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    transform: "perspective(1000px) rotateY(-5deg)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "perspective(1000px) rotateX(0deg)",
                    },
                  }}
                >
                  <Slider {...settings}>
                    {examImages.map((image, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <img
                          src={image.imageUrl}
                          alt={image.title}
                          style={{
                            width: "100%",
                            height: "480px",
                          }}
                        />
                      </Box>
                    ))}
                  </Slider>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ pt: 10, pb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#065A79",
              mb: 3,
              fontFamily: "Poppins, sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontOpticalSizing: "auto",
              fontStyle: "normal",
              letterSpacing: 1,
            }}
          >
            How It Works?
          </Typography>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                background: "rgba(255,255,255,0.9)",
                borderRadius: 4,
              }}
            >
              {/* <Stepper
                activeStep={-1}
                sx={{
                  gap: 2,
                  orientation: { md: "horizontal", sm: "vertical" },
                }}
              >
                {steps.map((step, index) => (
                  <Step key={index} active={true}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            background: step.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            transform: "scale(1)",
                            transition: "transform 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          {React.cloneElement(step.icon, {
                            sx: { fontSize: 20 },
                          })}
                        </Box>
                      )}
                      sx={{
                        "& .MuiStepLabel-label": {
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: step.color,
                        },
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper> */}
   

<Stepper
  activeStep={-1}
  orientation={isMobile ? "vertical" : "horizontal"}
  sx={{
    gap: 2,
    width: "100%",
    "& .MuiStep-root": {
      px: { xs: 0, sm: 1 },
    },
  }}
>
  {steps.map((step, index) => (
    <Step key={index} active>
      <StepLabel
        StepIconComponent={() => (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: step.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              transition: "0.3s",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            {React.cloneElement(step.icon, {
              sx: { fontSize: 20 },
            })}
          </Box>
        )}
        sx={{
          "& .MuiStepLabel-label": {
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
            fontWeight: 600,
            color: step.color,
          },
        }}
      >
        {step.label}
      </StepLabel>
    </Step>
  ))}
</Stepper>
            </Paper>
          </motion.div>
        </Box>

        {/* Features Grid */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              color: "#065A79",
              fontWeight: "bold",
              mb: 2,
              fontFamily: "Poppins, sans-serif",
              fontOpticalSizing: "auto",
              fontStyle: "normal",
              letterSpacing: 1,
            }}
          >
            Why Choose Our Platform?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              color: "#AD1C86",
              maxWidth: 700,
              mx: "auto",
              mb: 5,
              fontOpticalSizing: "auto",
              fontStyle: "normal",
              letterSpacing: 1,
            }}
          >
            Experience the future of examinations with our cutting-edge digital
            platform
          </Typography>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: "center",
                      height: "100%",
                      transition: "all 0.3s ease",
                      borderRadius: 3,
                      background: "white",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-10px)",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                        "& .feature-icon": {
                          transform: "scale(1.1) rotateY(360deg)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}40)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        transition: "all 0.6s ease",
                        color: feature.color,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", mb: 1, color: feature.color }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <Paper
            elevation={0}
            sx={{
              mt: 8,
              p: 5,
              textAlign: "center",
              background: "linear-gradient(135deg, #065A79 0%, #AD1C86 100%)",
              borderRadius: 4,
              color: "white",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
              Ready for Your Next Examination?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
              Download your admit card, appear in online exams, and access
              detailed report cards through the Shree Mangalam School Student
              Portal.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Link to="/online-exams" style={{ textDecoration: "none" }}>
                <CustomButton ButtonName={"Get Started Now"} />
              </Link>
            </Stack>
          </Paper>
        </motion.div>

        {/* Custom CSS for slider dots */}
        <style>
          {`
            .custom-dots li button:before {
              color: #065A79;
            }
            .custom-dots li.slick-active button:before {
              color: #AD1C86;
            }
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-20px); }
            }
          `}
        </style>
      </Container>
    </Box>
  );
};

export default ExamAbout;
